import React from "react";
import {
  CommercialPage,
  PageIntro,
  ContactCTA,
} from "../components/commercial";
import { commercialMetadata } from "./commercial-metadata";
export default function Engineering() {
  return (
    <CommercialPage meta={commercialMetadata.engineering}>
      <PageIntro
        label="Engineering"
        title="Understand the whole system. Own the difficult details."
      >
        The hardest problems often live between components, devices, networks,
        data and the physical world. We reason about the complete system:
        constraints, dependencies, failure modes and production behavior.
      </PageIntro>
      <section className="section expertise-section">
        <div className="container">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Deliberate architecture</p>
              <h2>Make the important decisions explicit.</h2>
            </div>
            <p>
              The objective is an architecture appropriate to the system’s real
              constraints.
            </p>
          </div>
          <div className="principles">
            {[
              [
                "Boundaries & state",
                "Which component owns each responsibility? Where does state live, and which interfaces must remain compatible?",
              ],
              [
                "Failure & recovery",
                "What happens when a dependency fails? Define timeouts, retries, degraded operation and recovery deliberately.",
              ],
              [
                "Production & operation",
                "Design for deployment, updates, rollback, configuration and diagnostics before the system reaches the field.",
              ],
              [
                "Constraints & trade-offs",
                "Balance reliability, latency, memory, bandwidth, cost and maintainability against the actual requirements.",
              ],
            ].map(([title, copy], i) => (
              <article key={title}>
                <span className="principle-number">0{i + 1} /</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="section container about-grid">
        <div>
          <p className="eyebrow">Cross-layer reasoning</p>
          <h2>A symptom in one layer can begin in another.</h2>
          <p className="page-copy">
            A device application can appear healthy while a network assumption,
            operating-system constraint or backend dependency makes the product
            unreliable.
          </p>
        </div>
        <div className="page-prose">
          <h3>Reliability is a system property.</h3>
          <p>
            Components must behave predictably together, including during
            partial failures. Validation, state consistency, observability and
            safe deployment belong in the design.
          </p>
          <h3>Work with the constraints.</h3>
          <p>
            Field devices may be difficult to reach. Existing installations may
            not be replaceable. Legacy interfaces and several software
            generations may need to coexist. We design around those realities.
          </p>
          <h3>Preserve what already works.</h3>
          <p>
            Modernization starts by understanding compatibility and operational
            requirements. The right intervention may be a migration, a new
            architecture, or one carefully chosen change.
          </p>
        </div>
      </section>
      <section className="section services-section">
        <div className="container about-grid">
          <div>
            <p className="eyebrow">Production AI</p>
            <h2>AI is still software engineering.</h2>
          </div>
          <div className="page-prose">
            <p>
              Models are components of a production system. Context, data,
              interfaces, latency, security, evaluation and failure handling
              remain engineering responsibilities.
            </p>
            <p>
              Probabilistic outputs make evaluation and explicit system
              boundaries essential. We ask what the system should do, where AI
              adds value, how to know whether it works, and what happens when it
              is wrong.
            </p>
            <a className="text-link" href="/services">
              Explore engineering services <span aria-hidden="true">↗</span>
            </a>
          </div>
        </div>
      </section>
      <section className="section container about-grid">
        <div>
          <p className="eyebrow">Senior technical ownership</p>
          <h2>Engineering habits shaped by demanding systems.</h2>
        </div>
        <div className="page-prose">
          <p>
            Quantingo engagements are technically led by Pablo Rego, a software
            engineer and systems architect with more than 20 years of
            experience.
          </p>
          <p>
            His background includes aerospace and defense software at Embraer,
            instrumentation, signal processing and AI/ML R&D at Halliburton, and
            end-to-end production software through Quantingo.
          </p>
          <p>
            That experience supports explicit interfaces, reproducible work,
            validated assumptions and attention to failure. The level of process
            remains appropriate to the consequences of failure.
          </p>
          <p>
            When a project needs additional engineering or specialist capacity,
            Quantingo retains technical coordination and responsibility for the
            contracted delivery.
          </p>
          <p>
            A paid Engineering Assessment follows qualification for every
            continuing opportunity, reducing uncertainty before the
            implementation Proposal/quotation.
          </p>
          <a className="text-link" href="/how-we-work">
            How we work <span aria-hidden="true">↗</span>
          </a>
        </div>
      </section>
      <ContactCTA
        title="Bring us the difficult part."
        label="Discuss an engineering problem"
      >
        If the problem crosses several parts of the stack, or you do not trust
        how a system will behave in production, that is a useful place to start.
      </ContactCTA>
    </CommercialPage>
  );
}
